<template>
  <TableList
    v-rect-loading="loading"
    noAction
    row-key="_id"
    class="applicant-status-auto-change-config-list"
    :title="$t(`${i18nPrefix}.title`)"
    :listData="configList"
    :tableColumns="tableColumns"
  >
    <template #filter>
      <el-select
        v-model="search.companyId"
        filterable
        class="company-filter"
        @change="searchCompanyList"
      >
      <el-option
        v-for="item in companyList"
        :key="item.id"
        :label="`${item.id} - ${item.i18nName}`"
        :style="{ paddingLeft: item.level * 15 + 'px' }"
        :value="item.id"
      />
      </el-select>
    </template>
    <template #btn>
      <el-button v-show="isDelete" class="create-group-btn create-btn" @click="deletedConfig">{{ $t(`${i18nPrefix}.btn.deleteConfig`) }}</el-button>
      <el-button v-show="isSave" class="create-group-btn create-btn" @click="saveConfig">{{ $t(`${i18nPrefix}.btn.saveConfig`) }}</el-button>
      <el-button v-show="isAdd" class="create-group-btn create-btn" @click="addConfig">{{ $t(`${i18nPrefix}.btn.addConfig`) }}</el-button>
      <el-button v-show="isAddAndSave" class="create-group-btn create-btn" @click="saveConfig">{{ $t(`${i18nPrefix}.btn.saveConfig`) }}</el-button>
    </template>
    <template #autoChangeToHeader>
      <span>{{ $t(`${i18nPrefix}.label.autoChangeTo`) }}</span>
      <span v-auto-tooltip.always="$t(`${i18nPrefix}.message.autoChangeToRuleTip`)" class="tip-message-icon">
        <i class="el-icon-question"></i>
      </span>
    </template>
    <template #dwellTime="{ row }">
      <el-input-number
        v-model="row.dwell_time"
        size="medium"
        controls-position="right"
        step-strictly
        class="input-number"
        :step="1"
        :class="row.error.dwellTime && 'has-error'"
        :min="0"
        :placeholder="$t(`${i18nPrefix}.placeholder.dwellTime`)"
        @change="onDwellTimeChange($event, row)"
      ></el-input-number>
    </template>
    <template #autoChangeTo="{ row }">
      <el-select
        v-model="row.auto_change_to"
        clearable
        size="medium"
        :class="row.error.autoChangeTo && 'has-error'"
        :disabled="!row.dwell_time"
        :placeholder="$t(`${i18nPrefix}.placeholder.autoChangeTo`)"
        @change="onAutoChangeStatusTo($event, row)"
      >
        <el-option
          v-for="(applicantStatus, index) in applicantStatusList"
          :key="index"
          :label="applicantStatus.i18n_name"
          :value="applicantStatus.status"
        ></el-option>
      </el-select>
    </template>
    <template #page-bottom>
      <DialogWrapper
        :show.sync="dialogVisible"
        :loading="dialogLoading"
        :title="$t(`${i18nPrefix}.message.confirmDelete`)"
        @save="handleConfirmDelete"
      ></DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { HIDE_STATUS } from '@/constant/application'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.applicantStatusAutoChange',
      companyList: [],
      configList: [],
      company: null,
      applicantStatusList: [],
      search: {
        companyId: null
      },
      isDelete: true,
      isSave: true,
      isAdd: false,
      isAddAndSave: false,
      dialogVisible: false,
      dialogLoading: false
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'i18n_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.statusDwellTime`),
          dataKey: 'dwell_time',
          slotName: 'dwellTime'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.autoChangeTo`),
          dataKey: 'auto_change_to',
          // headerSlotName: 'autoChangeToHeader',
          slotName: 'autoChangeTo'
        }
      ]
      return columns
    }
  },
  created () {
    this.getCompanyList()
  },
  methods: {
    ...mapActions('applicantStatusAutoChangeConfig', [
      'getCompanyApplicantStatusAutoChangeConfig',
      'deleteApplicantStatusAutoChangeConfig',
      'updateApplicantStatusAutoChangeConfig',
      'createApplicantStatusAutoChangeConfig'
    ]),
    ...mapActions('company', ['getCompanyChildrenTree']),
    flatCompanyChildrenTreeMap (resultList, company, treeMap) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      resultList.push(company)
      _.forEach(treeMap[company.id], child => {
        this.flatCompanyChildrenTreeMap(resultList, child, treeMap)
      })
    },
    async getCompanyList (companyId) {
      this.loading = true
      try {
        const resp = await this.getCompanyChildrenTree()
        if (resp) {
          const currentCompany = resp.self
          const companyList = []
          this.flatCompanyChildrenTreeMap(companyList, currentCompany, resp.children)
          this.companyList = companyList
          this.company = currentCompany
        }
        if (!companyId) {
          this.search.companyId = this.company.id
        } else {
          this.search.companyId = companyId
        }
        await this.searchCompanyList()
      } finally {
        this.loading = false
      }
    },
    async searchCompanyList () {
      this.loading = true
      try {
        if (!this.search.companyId) {
          return
        }
        const resp = await this.getCompanyApplicantStatusAutoChangeConfig({ companyId: this.search.companyId })
        if (resp) {
          const configList = resp.applicant_status_config
          this.applicantStatusList = []
          const hasSetConfig = _.some(configList, item => Boolean(item.dwell_time))
          if (hasSetConfig) {
            this.configList = _.map(configList, config => {
              addI18nProperty(config, 'i18n_name', 'name_cn', 'name_en')
              config.error = {
                dwellTime: false,
                autoChangeTo: false
              }
              const status = {
                name_cn: config.name_cn,
                name_en: config.name_en,
                status: config.status
              }
              addI18nProperty(status, 'i18n_name', 'name_cn', 'name_en')
              if (!_.includes(HIDE_STATUS, status.status)) {
                this.applicantStatusList.push(status)
              }
              return config
            })
            this.isDelete = true
            this.isSave = true
            this.isAdd = false
          } else {
            this.isDelete = false
            this.isSave = false
            this.isAdd = true
            this.configList = []
            this.applicantStatusList = []
          }
        }
      } finally {
        this.loading = false
      }
    },
    async addConfig () {
      this.loading = true
      try {
        if (!this.search.companyId) {
          return
        }
        const resp = await this.getCompanyApplicantStatusAutoChangeConfig({ companyId: this.search.companyId })
        if (resp) {
          this.configList = _.map(resp.applicant_status_config, config => {
            addI18nProperty(config, 'i18n_name', 'name_cn', 'name_en')
            config.error = {
              dwellTime: false,
              autoChangeTo: false
            }
            const status = {
              name_cn: config.name_cn,
              name_en: config.name_en,
              status: config.status
            }
            addI18nProperty(status, 'i18n_name', 'name_cn', 'name_en')
            if (!_.includes(HIDE_STATUS, status.status)) {
              this.applicantStatusList.push(status)
            }
            return config
          })
        }
        this.isAdd = false
        this.isSave = false
        this.isDelete = false
        this.isAddAndSave = true
      } finally {
        this.loading = false
      }
    },
    validateConfig (config, noErrorTip, allowEmpty) {
      const error = config.error
      _.forEach(Object.keys(error), (key) => {
        error[key] = false
      })
      const dwellTime = config.dwell_time
      const autoChangeTo = config.auto_change_to
      const errorMessage = []
      if (allowEmpty && !dwellTime && !autoChangeTo) {
        return false
      }
      if (!_.isInteger(_.toNumber(dwellTime))) {
        errorMessage.push(this.$t(`${this.i18nPrefix}.message.dwellTimeIsNotInt`))
        error.dwellTime = true
      }
      if (errorMessage.length > 0 && !noErrorTip) {
        this.$message({
          type: 'error',
          dangerouslyUseHTMLString: true,
          message: _.map(errorMessage, item => `<div>${item}</div>`).join('')
        })
      }
      return errorMessage.length > 0
    },
    async saveConfig () {
      if (!this.search.companyId) {
        return
      }
      this.loading = true
      let success = false
      try {
        const configList = this.configList
        let isInvalid = false
        let hasSet = false
        _.forEach(configList, config => {
          if (this.validateConfig(config, true, true)) {
            isInvalid = true
          }
          if (config.dwell_time) {
            hasSet = true
          }
        })
        if (!hasSet) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.cannotSaveEmpty`))
          return
        }
        if (isInvalid) {
          // this.$message.error(this.$t(`${this.i18nPrefix}.message.checkConfigList`))
          this.$nextTick(() => {
            this.$el.querySelector('.el-table .has-error').scrollIntoView({ block: 'center', behavior: 'smooth' })
          })
          return
        }
        const saveFunc = this.isAddAndSave ? this.createApplicantStatusAutoChangeConfig : this.updateApplicantStatusAutoChangeConfig
        const resp = await saveFunc({ companyId: this.search.companyId, configList })
        if (resp) {
          success = true
          this.isAddAndSave = false
        }
        await this.getCompanyList(this.search.companyId)
      } finally {
        this.loading = false
        if (success) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        }
      }
    },
    onAutoChangeStatusTo (autoChangeTo, config) {
      config.auto_change_to = autoChangeTo
      this.validateConfig(config)
    },
    onDwellTimeChange (dwellTime, config) {
      config.dwell_time = dwellTime
      this.validateConfig(config)
    },
    deletedConfig () {
      this.dialogVisible = true
    },
    async handleConfirmDelete () {
      if (!this.search.companyId) {
        return
      }
      this.dialogLoading = true
      let success = false
      try {
        const resp = await this.deleteApplicantStatusAutoChangeConfig(this.search.companyId)
        if (resp) {
          success = true
          this.dialogVisible = false
          await this.getCompanyList(this.search.companyId)
        }
      } finally {
        this.dialogLoading = false
        if (success) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.deleteSuccess`))
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.applicant-status-auto-change-config-list {
  .filter-group {
    .company-filter {
      width: 50%;
    }
  }

  .tip-message-icon {
    margin-left: 5px;
    font-size: 14px;
  }

  .input-number {
    ::v-deep {
      .el-input--medium {
        .el-input__inner {
          height: 34px;
          line-height: 34px;
        }
      }
    }
  }

  ::v-deep {
    .el-table {
      .has-error {
        .el-input__inner,
        .el-input__inner:focus {
          border-color: #f56c6c;
        }
      }

      .el-select {
        .el-input__inner {
          height: 34px;
          line-height: 34px;
          padding: 0 20px 0 5px;
          text-align: center;
        }

        .el-input__suffix {
          right: 0;
        }

        .el-input__icon {
          line-height: 28px;
          width: 22px;
        }
      }

      .el-input-number--medium {
        width: 80%;
      }

    }
  }
}
</style>
